import dayjs, { Dayjs } from 'dayjs';
import { FormatHelper } from './FormatHelper';

import MDF_LOCALE_DATA from '../localization/translation.json';

// Need en-ca, en-gb, es-us, fr-ca in order to do the localizations we need.
import 'dayjs/locale/en-ca';
import 'dayjs/locale/en-gb';
import 'dayjs/locale/es-us';
import 'dayjs/locale/fr-ca';

// Day.js plugins used in WFN
import advancedFormatPlugin from 'dayjs/plugin/advancedFormat';
import businessDaysPlugin from './BusinessDays';
import customParseFormatPlugin from 'dayjs/plugin/customParseFormat';
import dayOfYearPlugin from 'dayjs/plugin/dayOfYear';
import durationPlugin from 'dayjs/plugin/duration';
import isBetweenPlugin from 'dayjs/plugin/isBetween';
import isoWeekPlugin from 'dayjs/plugin/isoWeek';
import isSameOrAfterPlugin from 'dayjs/plugin/isSameOrAfter';
import isSameOrBeforePlugin from 'dayjs/plugin/isSameOrBefore';
import localeDataPlugin from 'dayjs/plugin/localeData';
import localizedFormatPlugin from 'dayjs/plugin/localizedFormat';
import objectSupportPlugin from 'dayjs/plugin/objectSupport';
import quarterOfYearPlugin from 'dayjs/plugin/quarterOfYear';
import relativeTimePlugin from 'dayjs/plugin/relativeTime';
import timezonePlugin from 'dayjs/plugin/timezone';
import utcPlugin from 'dayjs/plugin/utc';
import weekdayPlugin from 'dayjs/plugin/weekday';

dayjs.extend(advancedFormatPlugin);
dayjs.extend(businessDaysPlugin);
dayjs.extend(customParseFormatPlugin);
dayjs.extend(dayOfYearPlugin);
dayjs.extend(durationPlugin);
dayjs.extend(isBetweenPlugin);
dayjs.extend(isoWeekPlugin);
dayjs.extend(isSameOrAfterPlugin);
dayjs.extend(isSameOrBeforePlugin);
dayjs.extend(localeDataPlugin);
dayjs.extend(localizedFormatPlugin);
dayjs.extend(objectSupportPlugin);
dayjs.extend(quarterOfYearPlugin);
dayjs.extend(relativeTimePlugin);
dayjs.extend(timezonePlugin);
dayjs.extend(utcPlugin);
dayjs.extend(weekdayPlugin);

// Type for expanded locale configuration
export interface LocaleConfig {
  locale: string;
  dateFormat?: string;
}

// Type for date format option (used for preferences UI)
export interface DateFormatOption {
  pattern: string;
  label: string;
  example: string;
}

export class LocaleHelper {
  private static userLocale = 'en-us';
  private static localeData = MDF_LOCALE_DATA;
  private static defaultLocale = 'en-us';
  private static countryCode = 'us';
  private static cachedTranslations = [];
  private static userDateFormat: string | null = null;

  // The 9 user-selectable short date formats (matching iOS)
  static readonly SHORT_DATE_FORMATS: readonly DateFormatOption[] = [
    { pattern: 'M/D/YY', label: 'M/D/YY', example: '1/15/25' },
    { pattern: 'M/D/YYYY', label: 'M/D/YYYY', example: '1/15/2025' },
    { pattern: 'MM/DD/YYYY', label: 'MM/DD/YYYY', example: '01/15/2025' },
    { pattern: 'D/M/YY', label: 'D/M/YY', example: '15/1/25' },
    { pattern: 'DD/MM/YYYY', label: 'DD/MM/YYYY', example: '15/01/2025' },
    { pattern: 'DD.MM.YYYY', label: 'DD.MM.YYYY', example: '15.01.2025' },
    { pattern: 'DD-MM-YYYY', label: 'DD-MM-YYYY', example: '15-01-2025' },
    { pattern: 'YYYY/M/D', label: 'YYYY/M/D', example: '2025/1/15' },
    { pattern: 'YYYY.MM.DD', label: 'YYYY.MM.DD', example: '2025.01.15' },
    { pattern: 'YYYY-MM-DD', label: 'YYYY-MM-DD', example: '2025-01-15' }
  ] as const;

  // Default short date format by locale
  // Note: Using MM/DD with 4-digit years to match historical product behavior
  // TODO: Fill in the mapping for WFN's supported locales
  private static readonly LOCALE_DEFAULT_FORMATS: Record<string, string> = {
    // English locales
    'en-us': 'MM/DD/YYYY',
    'en-gb': 'DD/MM/YYYY',
    'en-ca': 'DD/MM/YYYY', // Same as en-gb, not ISO
    'en-au': 'DD/MM/YYYY',

    // Spanish locales
    'es-us': 'MM/DD/YYYY', // US Spanish uses US format (via es-pr substitution)
    'es-es': 'DD/MM/YYYY',
    'es-mx': 'DD/MM/YYYY',

    // French locales
    'fr-fr': 'DD/MM/YYYY',
    'fr-ca': 'YYYY-MM-DD' // Canadian French uses ISO

    // TODO: Add other supported locales as needed
  };

  // Fallback defaults by region (when locale not in LOCALE_DEFAULT_FORMATS)
  // Note: Using MM/DD with 4-digit years to match historical product behavior
  private static readonly REGION_DEFAULT_FORMATS: Record<string, string> = {
    us: 'MM/DD/YYYY',
    gb: 'DD/MM/YYYY',
    ca: 'YYYY-MM-DD',
    au: 'DD/MM/YYYY',
    jp: 'YYYY/MM/DD',
    de: 'DD.MM.YYYY',
    ch: 'DD.MM.YYYY',
    fr: 'DD/MM/YYYY'
  };

  // To get the user's locale
  static getUserLocale() {
    return LocaleHelper.userLocale;
  }

  static getCountryCode() {
    return LocaleHelper.countryCode;
  }

  private static normalizedLocaleData(localeData: any): any {
    const normalizedLocaleData = Object.assign({}, LocaleHelper.localeData);

    Object.keys(localeData).forEach((key) => {
      const normalizedKey = key.toLowerCase();

      if (LocaleHelper.localeData[normalizedKey]) {
        normalizedLocaleData[normalizedKey] = Object.assign(LocaleHelper.localeData[normalizedKey], localeData[key]);
      }
      else {
        normalizedLocaleData[normalizedKey] = localeData[key];
      }
    });

    return normalizedLocaleData;
  }

  // To set the locale data store
  static setLocaleData(data: any) {
    LocaleHelper.localeData = this.normalizedLocaleData(data);
  }

  // Initialize the user's locale. Call this from the application's controller.
  // Since this has to be async (due to appShell.getLocale() being async), calling it inside
  // some other function can cause async/await to go a bit viral in your application.
  // You can break that chain by doing:
  //   LocaleHelper.initializeLocale().then(() => 0);
  // This will let your code continue and avoid having the enclosing function need to be async.
  static async initializeLocale(value?: string, defaultLocale?: string, defaultCountryCode?: string) {
    // We have 4 ways to figure out the user's locale if the locale isn't provided above:
    // 1. The WFN Shell's getLocale() function
    // 2. The standard appShell's getLocale() function (which is async)
    // 3. The ADPLangLocaleCookie (which be handled by the first two, but just in case)
    // 4. The browser's language setting (either via navigator.languages or navigator.language)
    // 5. Default to en-us
    let cookieLocale;

    const localeMatches = document?.cookie.match(/ADPLangLocaleCookie=([^;]+)/);

    if (localeMatches && localeMatches[1]) {
      cookieLocale = localeMatches[1].replace('_', '-').toLowerCase();
    }

    const locale = value ??
      window?.['WFNShell']?.getLocale() ??
      (await window?.['getShell']?.().getLocale()) ??
      cookieLocale ??
      navigator?.languages?.[0] ?? navigator?.language ??
      'en-us';

    this.setLocale(locale, defaultLocale, defaultCountryCode);

    // Listen for changes to the locale
    window?.['SynergConfig']?.watchLocale((newLocale) => {
      let updatedLocale = newLocale.replace('_', '-').toLowerCase();

      // Fix Waypoint locales that are incomplete
      if (!updatedLocale.includes('-')) {
        switch (updatedLocale) {
          case 'en':
          case 'es':
            updatedLocale = updatedLocale + '-us';
            break;

          case 'fr':
            updatedLocale = updatedLocale + '-ca';
            break;

          default:
            console.error(`Locale ${updatedLocale} received from Waypoint is not a supported locale.`);
        }
      }

      LocaleHelper.setLocale(updatedLocale);
    });
  }

  // To set the locale data store
  // Accepts either a string (backward compatible) or LocaleConfig object
  static setLocale(value: string | LocaleConfig, defaultLocale?: string, defaultCountryCode?: string) {
    let locale: string;
    let dateFormat: string | null = null;

    if (typeof value === 'string') {
      locale = value;
    }
    else {
      locale = value.locale;
      dateFormat = value.dateFormat || null;
    }

    LocaleHelper.userLocale = locale.toLowerCase();
    LocaleHelper.userDateFormat = dateFormat;
    LocaleHelper.defaultLocale = defaultLocale || LocaleHelper.defaultLocale;
    LocaleHelper.countryCode = defaultCountryCode?.toLowerCase() || LocaleHelper.userLocale.split('-')[1];
    FormatHelper.updateCultureFormats(LocaleHelper.countryCode);
    dayjs.locale(locale.toLowerCase());

    // Update Waypoint with the new locale
    const SynergConfig = (window as any).SynergConfig;
    const waypointLocale = SynergConfig?.getLocale().toLowerCase();

    if (waypointLocale !== LocaleHelper.userLocale) {
      SynergConfig?.setLocale(LocaleHelper.userLocale);
    }

    // Update the html lang attribute
    const htmlLang = document?.documentElement?.lang;

    if (htmlLang?.toLowerCase() !== LocaleHelper.userLocale) {
      document?.documentElement?.setAttribute('lang', LocaleHelper.userLocale);
    }

    console.log(`LocaleHelper.setLocale(): User's locale is now ${LocaleHelper.userLocale}`);
  }

  // This method downloads the given translation URL and appends it in the localeData. If the given translation URL already available in localeData, then it skips downloading the content.
  static loadLocaleData(url: string) {
    // If the given url is already cached, then don't load the file again.
    if (LocaleHelper.cachedTranslations.includes(url)) {
      return Promise.resolve({});
    }

    return new Promise((resolve, reject) => {
      fetch(url)
        .then((response: Response) => {
          if (response.ok) { // fetch resolves the promise even if the response is 404
            return response.json();
          }
          else {
            return Promise.reject(`LocaleHelper.setLocaleDataFromUrl: Locale ${name} does not exist.`);
          }
        })
        .then((localeJson: any) => {
          const currentLocaleData = LocaleHelper.localeData;
          const normalizedLocaleJson = this.normalizedLocaleData(localeJson);
          LocaleHelper.localeData = Object.assign(currentLocaleData, normalizedLocaleJson);

          // Add the url as cached
          LocaleHelper.cachedTranslations.push(url);
          resolve(normalizedLocaleJson);
        })
        .catch((e) => {
          reject(e);
        });
    });
  }

  // To get the message from IDS key
  static getMessage(key: string, locale = LocaleHelper.userLocale): string {
    // Try user's locale, fallback to default locale
    if (LocaleHelper.localeData) {
      const usersLocaleMessage = LocaleHelper.localeData[locale];
      const defaultLocaleMessage = LocaleHelper.localeData[LocaleHelper.defaultLocale];
      return (usersLocaleMessage && usersLocaleMessage[key] || defaultLocaleMessage[key]);
    }
    else {
      console.error('Please load translations using LocaleHelper.setLocaleData()!');
    }
  }

  // To manage the global dayjs (was moment) object.
  // Deprecated: use LocaleHelper.dateAndTime getter instead as it doesn't result
  // in LocaleHelper.getMoment()(), which can look a bit strange.
  static getMoment() {
    return dayjs;
  }

  // Returns a handle to the common date and time helper API (dayjs)
  static get dateAndTime() {
    return dayjs;
  }

  static isDate(obj: any): boolean {
    // Convert obj to a boolean value before comparing so that the function always returns a boolean.
    return (!!obj) && obj instanceof Date;
  }

  static isDST(day?: Dayjs | Date): boolean {
    // Use the dayjs object passed in by the application or the default moment object
    const date: Dayjs = LocaleHelper.isDate(day) ? LocaleHelper.dateAndTime(day) : (day as Dayjs || LocaleHelper.dateAndTime());

    // Use the logic from moment's isDST() function by comparing the utcOffset of the provided date
    // with the utcOffset in month 0 (January - southern hemisphere) and 5 (June - northern hemisphere).
    return (date.utcOffset() > date.clone().month(0).utcOffset()) || (date.utcOffset() > date.clone().month(5).utcOffset());
  }

  // Get the short date format for the current user or a specific locale
  // The user's preference takes precedent, even if a locale is specified.
  // Otherwise, when locale is provided, returns that locale's default (for previewing other locales).
  // Finally, when locale is omitted, returns user's preference (explicit > localStorage > user's locale default)
  static getShortDateFormat(locale?: string): string {
    // Priority 1: localStorage for prototyping (remove when full UI is built)
    if (typeof localStorage !== 'undefined') {
      const storedFormat = localStorage.getItem('mdf-date-format');

      if (storedFormat) {
        return storedFormat;
      }
    }

    // Priority 2: Explicit setting via setLocale({ dateFormat: '...' })
    if (LocaleHelper.userDateFormat) {
      return LocaleHelper.userDateFormat;
    }

    // Priority 3: If a specific locale is requested, return its default format (no user preference)
    if (locale) {
      return LocaleHelper.getDefaultShortDateFormat(locale);
    }

    // Priority 4: User's locale default
    return LocaleHelper.getDefaultShortDateFormat();
  }

  // Get the default short date format for a specific locale or the current user's locale
  // TODO: Refine the fallback logic based on WFN's supported locales
  static getDefaultShortDateFormat(locale?: string): string {
    const targetLocale = locale?.toLowerCase() || LocaleHelper.userLocale;
    const region = targetLocale.split('-')[1] || LocaleHelper.countryCode;

    // Try full locale first (handles en-ca vs fr-ca correctly)
    if (LocaleHelper.LOCALE_DEFAULT_FORMATS[targetLocale]) {
      return LocaleHelper.LOCALE_DEFAULT_FORMATS[targetLocale];
    }

    // Fall back to region
    if (LocaleHelper.REGION_DEFAULT_FORMATS[region]) {
      return LocaleHelper.REGION_DEFAULT_FORMATS[region];
    }

    // Universal default: ISO format
    return 'YYYY-MM-DD';
  }

  // Detect if a Date object represents midnight UTC (common pattern for date-only values)
  // This helps avoid off-by-one-day bugs when formatting in western timezones
  private static isMidnightUtc(date: Date): boolean {
    return date.getUTCHours() === 0 &&
           date.getUTCMinutes() === 0 &&
           date.getUTCSeconds() === 0 &&
           date.getUTCMilliseconds() === 0;
  }

  // Format a date using the user's preferred short format or a specific locale's default
  // When locale is provided, uses that locale's default format (for previewing other locales)
  // Automatically detects midnight UTC dates and formats them in UTC to avoid timezone shift issues
  static formatShortDate(date: Date | Dayjs | string, locale?: string): string {
    const pattern = LocaleHelper.getShortDateFormat(locale);

    // For Date objects that are midnight UTC (date-only convention), format in UTC
    // to prevent off-by-one-day bugs in western timezones
    if (date instanceof Date && LocaleHelper.isMidnightUtc(date)) {
      return dayjs.utc(date).format(pattern);
    }

    return dayjs(date).format(pattern);
  }

  // Get all available short date format options (for preferences UI)
  static getShortDateFormatOptions(): readonly DateFormatOption[] {
    return LocaleHelper.SHORT_DATE_FORMATS;
  }
}
